import type {RouteRecordRaw} from 'vue-router';
import {createPropositionGuard, useProposition} from '@myparcel-frontend/proposition';
import {orderManagementGuard} from '../guards/order-management-guard';
import {orderRulesGuard} from '../guards/order-rules-guard';
import {useOrderVersion} from '@/composables/useOrderVersion/useOrderVersion';
import {useServiceAccount} from '@/composables/useServiceAccount/useServiceAccount';

export const shopSettingsRulesRoutes: RouteRecordRaw[] = [
  {
    name: 'shop-settings-rules',
    path: 'rules',
    component: () => import('../../../views/Settings/Shop/Rules/RulesSettings.vue'),
    meta: {
      title: (t) => t('rules.rule', 2),
    },
    redirect: (to) => {
      const {proposition} = useProposition();
      const {isOrderRulesAccessible} = useServiceAccount();
      const {hasOrderManagement} = useOrderVersion();

      const enabledOrderRules = proposition.value.settings.rules.enableOrderRules && isOrderRulesAccessible.value;

      const enabledShipmentRules = proposition.value.settings.rules.enableShipmentRules && hasOrderManagement;

      const enabledReturnRules = proposition.value.settings.rules.enableReturnRules && hasOrderManagement;

      if (enabledOrderRules) {
        return {name: 'shop-settings-rules-orders', params: to.params};
      }

      if (enabledShipmentRules) {
        return {name: 'shop-settings-rules-shipments', params: to.params};
      }

      if (enabledReturnRules) {
        return {name: 'shop-settings-rules-returns', params: to.params};
      }

      return {name: 'dashboard'};
    },
    children: [
      {
        name: 'shop-settings-rules-orders',
        path: 'orders',
        props: false,
        component: () => import('../../../views/Settings/Shop/Rules/OrderRules/OrderRules.vue'),
        beforeEnter: [
          createPropositionGuard((proposition) => proposition.settings.rules.enableOrderRules, {
            name: 'dashboard',
          }),
          orderRulesGuard,
        ],
        meta: {
          title: (t) => t('rules.orderRule', 2),
        },
      },
      {
        path: 'shipments',
        props: true,
        beforeEnter: [
          createPropositionGuard((proposition) => proposition.settings.rules.enableShipmentRules, {
            name: 'dashboard',
          }),
          orderManagementGuard,
        ],
        children: [
          {
            path: '',
            props: true,
            name: 'shop-settings-rules-shipments',
            component: () => import('../../../views/Settings/Shop/Rules/ShippingRules/ShippingRules.vue'),
            meta: {
              title: (t) => t('rules.shippingRule', 2),
            },
          },
          {
            path: 'create',
            props: true,
            name: 'shop-settings-rules-shipments-create',
            component: () =>
              import('../../../views/Settings/Shop/Shipment/ShippingRules/ShippingRuleCreate/ShippingRuleCreate.vue'),
            meta: {
              title: (t) => t('rules.legacy.shipping.create'),
              titleBarHeader: 'rules.legacy.shipping.create',
              hasTabMenu: false,
              rootRouteName: 'shop-settings-rules-shipments',
            },
          },
          {
            path: ':ruleId',
            name: 'shop-settings-rules-shipments-edit',
            props: true,
            component: () =>
              import('../../../views/Settings/Shop/Shipment/ShippingRules/ShippingRuleEdit/ShippingRuleEdit.vue'),
            meta: {
              title: (t) => t('rules.legacy.shipping.edit'),
              titleBarHeader: 'rules.legacy.shipping.edit',
              hasTabMenu: false,
              rootRouteName: 'shop-settings-rules-shipments',
            },
          },
        ],
      },
      {
        path: 'returns',
        beforeEnter: [
          createPropositionGuard((proposition) => proposition.settings.rules.enableReturnRules, {
            name: 'dashboard',
          }),
          orderManagementGuard,
        ],
        children: [
          {
            path: '',
            props: true,
            name: 'shop-settings-rules-returns',
            component: () => import('@/views/Settings/Shop/Rules/ReturnRules/ReturnRules.vue'),
            meta: {
              permissions: ['updateShopReturnSettings'],
              title: (t) => t('rules.returnRule', 2),
            },
          },
          {
            path: 'create',
            props: true,
            name: 'shop-settings-rules-returns-create',
            component: () => import('@/views/Settings/Shop/Returns/RulesCreate/RulesCreate.vue'),
            meta: {
              permissions: ['updateShopReturnSettings'],
              titleBarHeader: 'rules.legacy.return.create',
              title: (t) => t('rules.legacy.return.create'),
              hasTabMenu: false,
              rootRouteName: 'shop-settings-rules-returns',
            },
          },
          {
            path: ':ruleId',
            name: 'shop-settings-rules-returns-edit',
            props: true,
            component: () => import('@/views/Settings/Shop/Returns/RulesEdit/RulesEdit.vue'),
            meta: {
              permissions: ['updateShopReturnSettings'],
              titleBarHeader: 'rules.legacy.return.edit',
              title: (t) => t('rules.legacy.return.edit'),
              hasTabMenu: false,
              rootRouteName: 'shop-settings-rules-returns',
            },
          },
        ],
      },
    ],
  },
];
